/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { globalProductSearch, inStoreProductSearch } from '@/lib/actions/globalSearchAction'
import Icons from '@/components/icons'
import Spinner from '@/components/Spinner'
import { getSEOFriendlyURL } from '@/lib/utilities/Utils'
import useIsMobile from '@/hooks/useIsMobile'
import dynamic from 'next/dynamic'
import { getInstockProductsFirst } from '@/utils/getInstockProductsFirst'

// Dynamic imports
const ProductCard = dynamic(async () => await import('@/components/common/product-card'), { loading: () => <></> })
const HorizontalScrollWithArrows = dynamic(async () => await import('@/routes/Common/horizontal-scroll'), {
  ssr: false,
  loading: () => <></>
})

interface SearchDataType {
  data: []
  count: number
  totalCount: number
  combinationFilters: []
  popularProducts: {
    popularProducts: []
  }
}

interface StateType {
  searchText: string
  loading: boolean
  error: boolean
  searchData: SearchDataType | null
  page: number
  lazyLoader: boolean
  inStoreSearchData: any
  showMoreFilters: boolean
  productCategories: string
  deliveryType: string
  selectedOfferType: string
  discountPercentageMin: string
  discountPercentageMax: string
  selectedQuickFilters: any[]
}

const GlobalSearch = () => {
  const router = useRouter()
  const [state, setState] = useState<StateType>({
    searchText: '',
    loading: true,
    error: false,
    searchData: null,
    page: 1,
    lazyLoader: false,
    inStoreSearchData: null,
    showMoreFilters: false,
    productCategories: '',
    deliveryType: '',
    selectedOfferType: '',
    discountPercentageMin: '',
    discountPercentageMax: '',
    selectedQuickFilters: []
  })
  const prevSearchTextRef = useRef('')
  const isMobile = useIsMobile()

  const {
    searchText,
    loading,
    error,
    searchData,
    page,
    lazyLoader,
    inStoreSearchData,
    showMoreFilters,
    productCategories,
    deliveryType,
    // selectedOfferType,
    discountPercentageMin,
    discountPercentageMax,
    selectedQuickFilters
  } = state

  useEffect(() => {
    if (!router.isReady) return // check if router is ready
    const { q = '' } = router.query
    if (searchText !== q) {
      setState({
        ...state,
        searchText: String(q),
        loading: true,
        page: 1,
        searchData: null
      })
    }
  }, [router.query])

  useEffect(() => {
    if (searchText !== '') {
      loadSearchProducts()
      loadInStoreProductSearch(65300)
    }
  }, [searchText])

  useEffect(() => {
    const prevSearchText = prevSearchTextRef.current
    // const prevSelectedAddress = prevSelectedAddressRef.current;

    if (searchText !== prevSearchText) {
      resetFilters()
    }

    prevSearchTextRef.current = searchText
    // prevSelectedAddressRef.current = selectedAddress;
  }, [searchText])

  const resetFilters = () => {
    setState({
      ...state,
      page: 1,
      productCategories: '',
      deliveryType: '',
      selectedOfferType: '',
      discountPercentageMin: '',
      discountPercentageMax: '',
      inStoreSearchData: null
    })
    loadSearchProducts()
  }

  const loadSearchProducts = () => {
    globalProductSearch(
      {
        searchText,
        productCategories,
        brandId: '',
        productId: '',
        discountPercentageMin,
        discountPercentageMax,
        deliveryType,
        limit: 5,
        page
      }
    ).then((res) => {
      let newSearchData = res?.data
      if (page > 1) {
        newSearchData = {
          ...res.data,
          data: [
            ...searchData?.data ?? [],
            ...res?.data?.data
          ],
          count: searchData?.count + res?.data?.count
        }
      }
      setState({
        ...state,
        loading: false,
        error: false,
        lazyLoader: false,
        searchData: newSearchData
      })
    }).catch(() => {
      setState({
        ...state,
        loading: false,
        error: true
      })
    })
  }

  const loadInStoreProductSearch = (storeId: number) => {
    inStoreProductSearch(
      storeId,
      {}
    ).then((res) => {
      setState((prev) => ({
        ...prev,
        inStoreSearchData: res?.data
      }))
      // const newSearchData = searchData?.data?.filter((store: any) => (store?.id === storeId)) ?? []
      // setState({
      //   ...state,
      //   searchData: {
      //     ...searchData,
      //     data: newSearchData
      //   }
      // })
    }).catch(() => {
      //
    })
  }

  const toggleQuickFilter = (item: any, filterId: string) => {
    if (selectedQuickFilters.includes(filterId)) {
      const index = selectedQuickFilters.findIndex((id) => id === filterId)
      selectedQuickFilters.splice(index, 1)
      item.filters.forEach((filter: any) => {
        setState({
          ...state,
          [filter?.key]: '',
          page: 1
        })
      })
      setTimeout(() => {
        loadSearchProducts()
      }, 200)
    } else {
      selectedQuickFilters.push(filterId)
      item.filters.forEach((filter: any) => {
        setState({
          ...state,
          [filter?.key]: filter?.value,
          page: 1
        })
      })
      setTimeout(() => {
        loadSearchProducts()
      }, 200)
    }
  }

  const handleLazyLoad = (e: any) => {
    const {
      scrollTop,
      offsetHeight,
      scrollHeight,
      id
    } = e.target
    if (id !== 'global-search-section') {
      return
    }
    if (!loading && searchData !== null && (searchData?.count < searchData?.totalCount) &&
      (scrollHeight - offsetHeight - scrollTop < 10 ||
        offsetHeight + scrollTop === scrollHeight
      )
    ) {
      setState({
        ...state,
        page: page + 1,
        lazyLoader: true
      })
      setTimeout(() => {
        loadSearchProducts()
      }, 200)
    }
  }

  return (
    <div className={`container px-2 sm:px-0 mx-${isMobile ? '0' : 'auto'}`}>
      {
        (searchData != null) && (
          <div className='mb-2 mt-4 flex justify-between flex-wrap'>
            {
              searchData?.data?.length > 0
                ? (
                  <span className={`font-bold truncate max-w-[450px] ${isMobile ? 'mb-2' : ''}`}>
                    Showing
                    &nbsp;
                    {inStoreSearchData !== null ? '1' : searchData?.totalCount}
                    &nbsp;
                    Results For
                    &nbsp;
                    <span className='font-bold text-pink-600 italic'>&quot;{searchText}&quot;</span>
                  </span>
                  )
                : (
                  <span className={`font-bold truncate max-w-[450px] border-b-2 ${isMobile ? 'mb-2' : ''}`}>
                    Sorry, we could not find any results for
                    &nbsp;
                    <span className='font-bold text-pink-600 italic'>&quot;{searchText}&quot;</span>
                  </span>
                  )
            }
            {
              searchData?.combinationFilters?.length > 0 && (
                <div className='flex font-bold text-[14px] gap-6'>
                  {
                    searchData?.combinationFilters?.map((filter: any) => (
                      <div
                        key={filter?.filter_name}
                      >
                        <button
                          className={`flex items-center border py-1 px-3 rounded-lg
                            ${selectedQuickFilters.includes(filter?.filter_name) ? 'border-pink-600 bg-pink-50' : ''}`}
                          onClick={() => {
                            toggleQuickFilter(filter, filter?.filter_name)
                          }}
                        >
                          {filter?.filter_name}
                          &nbsp;
                          {
                            selectedQuickFilters.includes(filter?.filter_name) && (
                              <Icons
                                name="close"
                                fill="#000000"
                              />
                            )
                          }
                        </button>
                      </div>
                    ))
                  }
                  <div>
                    <button
                      className='py-1 px-3 border rounded-lg'
                      onClick={() => {
                        setState({
                          ...state,
                          showMoreFilters: !showMoreFilters
                        })
                      }}
                    >
                      More
                    </button>
                  </div>
                </div>
              )
            }
          </div>
        )
      }
      <div>
        {(() => {
          let searchProductJsx = null
          if (loading && !lazyLoader) {
            searchProductJsx = (
              <div>
                <Spinner/>
              </div>
            )
          } else if (!loading && searchData === null && error) {
            searchProductJsx = (
              <div>
                <h2>Something went wrong</h2>
              </div>
            )
          } else if (searchData !== null) {
            searchProductJsx = (
              <>
                {
                  searchData?.data?.length > 0 && (
                    <div
                      id="global-search-section"
                      className="relative overflow-y-scroll scrollbar-none overflow-x-hidden max-h-[85vh] lg:max-h-[85vh] max-lg:max-h-[80vh]"
                      onScroll={(e) => {
                        if (inStoreSearchData === null) {
                          handleLazyLoad(e)
                        }
                      }}
                    >
                      {
                        searchData?.data?.map((stores: any) => (
                          <div
                            key={stores?.id}
                          >
                            <div className='border-b'>
                              <div className='flex justify-between items-center my-4'>
                                <div>
                                  <span className='font-bold'>
                                    {stores?.name}
                                  </span>
                                  <div className='flex items-center text-[12px] font-bold'>
                                    <span>
                                      <Icons
                                        name="star3"
                                      />
                                    </span>
                                    <span className='mx-0.5'>
                                      {(stores?.rating > 0 && stores?.rating?.toFixed(2)) ?? '--'}
                                    </span>
                                    <span className='mt-[-5px] mx-1'>.</span>
                                    <span>
                                      <Icons
                                        name="bike"
                                      />
                                    </span>
                                    <span className='mx-0.5'>
                                      {stores?.averageDeliveryTimeString ?? '--'}
                                    </span>
                                    <span className='mt-[-5px] mx-1'>.</span>
                                    <span>
                                      {(stores.calculatedDistance / 1000).toFixed(2) ?? '--'}
                                      Km
                                    </span>
                                    {
                                      Boolean(stores?.isFreeSampleStore) && (
                                        <div className='flex'>
                                          <span className='mt-[2px] mx-1.5'>.</span>
                                          <Icons
                                            name="free-sample"
                                          />
                                        </div>
                                      )
                                    }
                                  </div>
                                </div>
                                <div>
                                  {/*  */}
                                  <button
                                    className='font-bold text-pink-600 flex justify-center gap-1'
                                    onClick={() => {
                                      // Need backend changes on this api
                                      // Need store info like city, locality etc
                                      router.push(`/mumbai/andheri/grocery/${getSEOFriendlyURL(stores?.name)}-${stores?.id}`.toLocaleLowerCase()).catch(() => {
                                      })
                                    }}
                                  >
                                    Visit Store
                                    <Icons name="circle-arrow" fill="#B8238E" stroke="#ffffff" width="1.3rem"/>
                                  </button>
                                </div>
                              </div>
                              <div className='d-flex mb-6'>
                                <HorizontalScrollWithArrows>
                                  {
                                    getInstockProductsFirst(stores?.productsWithMeasurements)?.map((product: any) => (
                                      <div
                                        key={product?.retailerProductId}
                                      >
                                        <ProductCard
                                          item={product}
                                          onSuccess={() => {
                                            loadInStoreProductSearch(stores?.id)
                                          }}
                                        />
                                      </div>
                                    ))
                                  }
                                </HorizontalScrollWithArrows>
                                <HorizontalScrollWithArrows>
                                  {
                                    stores?.comboOffers?.map((product: any) => (
                                      <div
                                        key={product?.retailerProductId}
                                      >
                                        <ProductCard
                                          item={product}
                                          onSuccess={() => {
                                            loadInStoreProductSearch(stores?.id)
                                          }}
                                          isOfferProduct
                                        />
                                      </div>
                                    ))
                                  }
                                </HorizontalScrollWithArrows>
                                <HorizontalScrollWithArrows>
                                  {
                                    stores?.relatedProducts?.map((product: any) => (
                                      <div
                                        key={product?.retailerProductId}
                                      >
                                        <ProductCard
                                          item={product}
                                          isRelatedProduct
                                        />
                                      </div>
                                    ))
                                  }
                                </HorizontalScrollWithArrows>
                              </div>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  )
                }
                {
                  lazyLoader && (
                    <div className='py-4'>
                      <Spinner/>
                    </div>
                  )
                }
                {
                  searchData?.data?.length > 0 && inStoreSearchData !== null &&
                  inStoreSearchData?.productsWithMeasurements?.length > 0 && (
                    <div className='flex'>
                      <div className='border-b py-4'>
                        <div className='font-bold'>
                          More Product From This Store
                        </div>
                        <div className='flex'>
                          <HorizontalScrollWithArrows>
                            {
                              inStoreSearchData?.productsWithMeasurements?.map((product: any) => (
                                <div
                                  key={product?.retailerProductId}
                                >
                                  <ProductCard
                                    item={product}
                                  />
                                </div>
                              ))
                            }
                          </HorizontalScrollWithArrows>
                        </div>
                      </div>
                    </div>
                  )
                }
                {
                  searchData?.data?.length === 0 &&
                  searchData?.popularProducts?.popularProducts?.length === 0 &&
                  inStoreSearchData === null && (
                    <div className='flex items-center justify-center'>
                      No Result Found
                    </div>
                  )
                }
              </>
            )
          }
          return searchProductJsx
        })()}
      </div>
    </div>
  )
}

export default GlobalSearch
